import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { DeleteExpensesToastMessage } from 'src/app/Toast/Toast'
import {
  selectDeletingExpenseFromState,
  selectDeletingExpenseTopLevelError,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.selectors'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import { useTranslate } from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import { expenseReportUrl } from 'src/navigation/urls'
import { useDeleteExpense } from 'src/queries/expenses.mutations'
import { createSetIsDeleteExpensePopupOpen } from 'src/redux/actions/app.actions'
import { selectSelectedDeleteExpensesFromState } from 'src/redux/selectors/data/expenses.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const DeleteExpensePopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const expenses = useSelector(selectSelectedDeleteExpensesFromState)
  const location = useSelector(selectLocationFromState)
  // deletingExpense/topLevelError stay redux-backed: the mutation preserves the DELETE_EXPENSE_INIT/SUCCESS/FAILURE dispatches the UpdateExpenseFlyout spinner reads.
  const deletingExpense = useSelector(selectDeletingExpenseFromState)
  const topLevelError = useSelector(selectDeletingExpenseTopLevelError)
  const { analytics } = useAnalyticsContext()

  // Success side-effects live on the hook's onSuccess, not mutate()'s: this popup unmounts on success and mutate() callbacks drop mid-unmount (src/queries/AGENTS.md).
  const deleteExpenseMutation = useDeleteExpense({
    onSuccess: (report, { expenseIds }) => {
      dispatch(createSetIsDeleteExpensePopupOpen(false))
      dispatch(closeModal())
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <DeleteExpensesToastMessage expensesCount={expenseIds.length} />
          ),
        }),
      )
      const isBulkDelete =
        matchPath(location.pathname, expenseReportUrl(report.id)) !== null
      analytics?.track(
        isBulkDelete
          ? 'out of pocket expense report details page - bulk delete - button press'
          : 'update out of pocket expense report flyout - delete expense - button press',
        {
          interaction: 'button press',
          intent: 'delete expense',
          Total_expenses_deleted: isBulkDelete ? expenseIds.length : undefined,
        },
      )
    },
  })

  const subTitleTopText =
    expenses !== undefined &&
    (expenses.length === 1
      ? translate('You are about to delete 1 expense.')
      : translate('You are about to delete ${count} expenses.', {
          count: expenses.length.toString(),
        }))

  const dispatchDeleteExpense = (): void => {
    if (expenses === undefined) return
    deleteExpenseMutation.mutate({ expenseIds: expenses })
  }

  const closePopup = (): void => {
    dispatch(createSetIsDeleteExpensePopupOpen(false))
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={translate('Delete Expense')}
        subtitle={
          <>
            <p
              css={css`
                margin-bottom: 20px;
              `}
            >
              {subTitleTopText}
            </p>
            <p
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('This action cannot be undone.')}
            </p>
          </>
        }
        actionButtonLabel={translate('Confirm and Delete')}
        actionButtonOnClick={dispatchDeleteExpense}
        actionButtonLoading={deletingExpense}
        actionButtonVariant="severe"
        topLevelError={topLevelError}
        isCancelButtonLink
        underline={false}
        cancelButtonOnClick={closePopup}
        cancelButtonLabel={translate('Cancel')}
      />
    </Popup>
  )
}

export default DeleteExpensePopup
